import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * `RouteBlock` — a pickup → dropoff route summary used on order details.
 *
 * Each `Stop` is a location row (title + optional address) led by a small dot
 * marker, and stops are joined by a vertical dotted connector. The connector is
 * drawn under every marker and hidden on the final stop via a `last-child`
 * selector on the marker column, so the composed block reads as a single line
 * from the first pickup dot to the last dropoff dot.
 *
 * Presentational only — every label/address comes in via props; the component
 * knows nothing about orders, addresses, or couriers.
 *
 * Two composition paths, both supported:
 *
 * ```tsx
 * // Explicit children:
 * <RouteBlock>
 *   <RouteBlock.Stop variant="pickup" title="Магазин" address="ул. Пушкина, 10" />
 *   <RouteBlock.Stop variant="dropoff" title="Клиент" address="ул. Ленина, 42" />
 * </RouteBlock>
 *
 * // Data-driven convenience (used when no children are passed):
 * <RouteBlock
 *   pickup={{ title: 'Магазин', address: 'ул. Пушкина, 10' }}
 *   dropoff={{ title: 'Клиент', address: 'ул. Ленина, 42' }}
 * />
 * ```
 */
const routeStopMarkerVariants = cva('size-2.5 shrink-0 rounded-full', {
  variants: {
    variant: {
      pickup: 'bg-status-active',
      dropoff: 'bg-status-success',
    },
  },
  defaultVariants: {
    variant: 'pickup',
  },
});

type RouteStopVariant = NonNullable<
  VariantProps<typeof routeStopMarkerVariants>['variant']
>;

/** Data shape for the convenience `pickup`/`dropoff` props. */
interface RouteStopData {
  title: React.ReactNode;
  address?: React.ReactNode;
}

interface RouteBlockStopProps
  extends
    Omit<React.ComponentProps<'div'>, 'title'>,
    VariantProps<typeof routeStopMarkerVariants> {
  variant: RouteStopVariant;
  title: React.ReactNode;
  address?: React.ReactNode;
}

function RouteBlockStop({
  className,
  variant,
  title,
  address,
  ...props
}: RouteBlockStopProps) {
  return (
    <div
      data-slot="route-block-stop"
      data-variant={variant}
      className={cn('group flex gap-3', className)}
      {...props}
    >
      <div className="flex flex-col items-center">
        <span
          aria-hidden="true"
          className={cn(routeStopMarkerVariants({ variant }), 'mt-1')}
        />
        <span
          aria-hidden="true"
          className="mt-1 w-px flex-1 border-l border-dashed border-border group-last:hidden"
        />
      </div>
      <div className="flex flex-col gap-0.5 pb-6 group-last:pb-0">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {address !== undefined ? (
          <span className="text-xs text-muted-foreground">{address}</span>
        ) : null}
      </div>
    </div>
  );
}

interface RouteBlockProps extends React.ComponentProps<'div'> {
  /** Convenience data-driven pickup stop — rendered only when no children. */
  pickup?: RouteStopData;
  /** Convenience data-driven dropoff stop — rendered only when no children. */
  dropoff?: RouteStopData;
}

function RouteBlock({ className, children, pickup, dropoff, ...props }: RouteBlockProps) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div data-slot="route-block" className={cn('flex flex-col', className)} {...props}>
      {hasChildren ? (
        children
      ) : (
        <>
          {pickup !== undefined ? (
            <RouteBlockStop
              variant="pickup"
              title={pickup.title}
              {...(pickup.address !== undefined ? { address: pickup.address } : {})}
            />
          ) : null}
          {dropoff !== undefined ? (
            <RouteBlockStop
              variant="dropoff"
              title={dropoff.title}
              {...(dropoff.address !== undefined ? { address: dropoff.address } : {})}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

const RouteBlockNamespace = Object.assign(RouteBlock, {
  Stop: RouteBlockStop,
});

export { RouteBlockNamespace as RouteBlock, routeStopMarkerVariants };
export type { RouteBlockProps, RouteBlockStopProps, RouteStopData, RouteStopVariant };
